import { t } from "i18next";
import { lazy } from "react";
import type { HelpEntry } from "@/components/help/help.types";

/**
 * Help for the vehicle cards, in the CONTEXT.md glossary's words. Shipped in
 * public bundles, so nothing here may be unfit for a Client to read.
 */
export const VEHICLE_HELP_ENTRIES = {
	"vehicle.lifecycle.operationalStatus": {
		title: () => t("Operational status"),
		what: () =>
			t(
				"Whether drivers can book this vehicle. Onboarding: nobody can book it yet. Active: it can be booked as its calendar allows. Phased out: it's hidden from drivers."
			),
		why: () =>
			t(
				"In the wrong status, a vehicle either stays out of reach for drivers who need it, or shows up before it's ready."
			),
		when: () =>
			t(
				"Set Active once the vehicle is ready to drive. Set Phased out when it should stop taking new bookings. Phased out doesn't take it out of the fleet or cancel bookings."
			),
	},
	"vehicle.lifecycle.alwaysAvailable": {
		title: () => t("Always available"),
		what: () =>
			t(
				"Sets how the vehicle's calendar reads. On: it can be booked unless a Blocked window says otherwise. Off: it can only be booked inside the Available windows its Owner opens."
			),
		why: () =>
			t(
				"Switching it flips the whole calendar. On by mistake, the vehicle is open to bookings its Client didn't plan for. Off by mistake, nobody can book it until its Owner opens Available windows."
			),
		when: () =>
			t(
				"On for a typical fleet car that's free unless blocked. Off for a vehicle that's only shared at times its Owner chooses."
			),
		Guide: lazy(() =>
			import("./always-available-guide").then((module) => ({
				default: module.AlwaysAvailableGuide,
			}))
		),
	},
	"vehicle.lifecycle.plannedOutOfFleetDate": {
		title: () => t("Planned out-of-fleet date"),
		what: () =>
			t(
				"The day this vehicle is planned to leave the fleet. From this day on nobody can book the vehicle, the Owner and BattAdmins included, whatever its Operational status."
			),
		why: () =>
			t(
				"Setting or moving it earlier cancels every booking that ends after it, and its bookers only get a push notification. Left in place after the car stays, it keeps the car unbookable."
			),
		when: () =>
			t(
				"Set it once the leave date is confirmed, usually the lease end, and warn the bookers it affects first. Remove it if the car stays after all."
			),
	},
	"vehicle.lifecycle.outOfFleetSince": {
		title: () => t("Out of fleet since"),
		what: () =>
			t(
				"The day this vehicle really left the fleet. It's set by Put out of fleet and can't be changed or removed."
			),
		why: () =>
			t(
				"Once it's set, nobody can book the vehicle any more, whatever its Operational status or planned out-of-fleet date."
			),
		when: () =>
			t(
				"Only on the day the car physically leaves. Until then, use the planned out-of-fleet date."
			),
	},
} satisfies Record<`vehicle.${string}`, HelpEntry>;

export type VehicleHelpKey = keyof typeof VEHICLE_HELP_ENTRIES;
